import React from 'react';
import Modal from '../../../../_components/Modal';
import Button from '../../../../_components/Button';
import { updateCreditCard, getIuguConfig } from '../../../../api/plans';

// ─── Cache em memória do account_id da Iugu ──────────────────────────────────
// Buscado do backend na primeira vez que o modal abre. O backend por sua vez
// busca via /v1/profile usando o IUGU_API_TOKEN já existente.
// Nenhuma variável de ambiente nova no frontend ou no backend.
let _cachedIuguAccountId = null;

async function fetchIuguAccountId() {
  if (_cachedIuguAccountId) return _cachedIuguAccountId;
  try {
    const data = await getIuguConfig();
    _cachedIuguAccountId = data.account_id || '';
    return _cachedIuguAccountId;
  } catch (e) {
    return '';
  }
}

const IUGU_TOKEN_URL = 'https://api.iugu.com/v1/payment_token';

// ─── Estilos inline (zero libs extras) ───────────────────────────────────────
const S = {
  securityBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: 8,
    padding: '10px 14px',
    marginBottom: 20,
    fontSize: 13,
    color: '#15803d',
  },
  securityIcon: { fontSize: 16 },
  form: { display: 'flex', flexDirection: 'column', gap: 14 },
  row: { display: 'flex', gap: 12 },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: 4, flex: 1 },
  label: { fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 2 },
  input: {
    border: '1px solid #d1d5db',
    borderRadius: 8,
    padding: '10px 12px',
    fontSize: 14,
    outline: 'none',
    width: '100%',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  },
  inputFocus: { borderColor: '#3b82f6' },
  cardPreview: {
    background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)',
    borderRadius: 12,
    padding: '18px 20px',
    color: 'white',
    marginBottom: 20,
    fontSize: 13,
    letterSpacing: 1,
    minHeight: 80,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  cardNumber: { fontSize: 18, letterSpacing: 3, fontWeight: 700, fontFamily: 'monospace' },
  cardMeta: { display: 'flex', justifyContent: 'space-between', opacity: 0.85, marginTop: 10 },
  errorBox: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#b91c1c',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 13,
    marginTop: 4,
  },
  successBox: {
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    color: '#15803d',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 13,
    marginTop: 4,
    textAlign: 'center',
  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatCardNumber(value) {
  return value.replace(/\D/g, '').slice(0, 16).replace(/(.{4})/g, '$1 ').trim();
}
function formatExpiry(value) {
  const digits = value.replace(/\D/g, '').slice(0, 4);
  if (digits.length >= 3) return digits.slice(0, 2) + '/' + digits.slice(2);
  return digits;
}
function maskDisplay(number) {
  const digits = number.replace(/\D/g, '');
  if (!digits) return '**** **** **** ****';
  const padded = digits.padEnd(16, '*');
  return padded.replace(/(.{4})/g, '$1 ').trim();
}

// ─── Componente Principal ─────────────────────────────────────────────────────
export default function ChangeCreditCardModal({ show, onHide, onSuccess }) {
  const [form, setForm] = React.useState({
    number: '',
    name: '',
    expiry: '',
    cvv: '',
  });
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState(false);
  const [focused, setFocused] = React.useState('');

  function handleChange(e) {
    const { name, value } = e.target;
    setError('');
    if (name === 'number') {
      setForm(function (prev) { return Object.assign({}, prev, { number: formatCardNumber(value) }); });
    } else if (name === 'expiry') {
      setForm(function (prev) { return Object.assign({}, prev, { expiry: formatExpiry(value) }); });
    } else if (name === 'cvv') {
      setForm(function (prev) { return Object.assign({}, prev, { cvv: value.replace(/\D/g, '').slice(0, 4) }); });
    } else {
      setForm(function (prev) { return Object.assign({}, prev, { [name]: value }); });
    }
  }

  function handleClose() {
    if (loading) return;
    setForm({ number: '', name: '', expiry: '', cvv: '' });
    setError('');
    setSuccess(false);
    onHide();
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    // Validações básicas
    const digits = form.number.replace(/\D/g, '');
    if (digits.length < 13) { setError('Número do cartão inválido.'); return; }
    if (!form.name.trim()) { setError('Informe o nome impresso no cartão.'); return; }
    const [expMonth, expYear] = form.expiry.split('/');
    if (!expMonth || !expYear || expMonth < 1 || expMonth > 12 || expYear.length < 2) {
      setError('Data de validade inválida (MM/AA).'); return;
    }
    if (form.cvv.length < 3) { setError('CVV inválido.'); return; }

    setLoading(true);

    try {
      // ─── Busca o account_id público do backend (obtido via /v1/profile da Iugu) ──
      const accountId = await fetchIuguAccountId();
      if (!accountId) {
        setError('Não foi possível conectar ao gateway de pagamento. Tente novamente.');
        setLoading(false);
        return;
      }

      // ─── Passo 1: Tokenizar DIRETO na Iugu, do navegador do cliente ──────
      // Os dados brutos do cartão vão apenas para api.iugu.com — nunca para o nosso servidor.
      const iuguPayload = {
        account_id: accountId,
        method: 'credit_card',
        test: process.env.NODE_ENV !== 'production',
        data: {
          number: digits,
          verification_value: form.cvv,
          first_name: form.name.split(' ')[0],
          last_name: form.name.split(' ').slice(1).join(' ') || form.name.split(' ')[0],
          month: expMonth,
          year: expYear.length === 2 ? '20' + expYear : expYear,
        },
      };

      const iuguRes = await fetch(IUGU_TOKEN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(iuguPayload),
      });
      const iuguData = await iuguRes.json();

      if (!iuguRes.ok || iuguData.errors) {
        const msgs = iuguData.errors
          ? Object.values(iuguData.errors).flat().join(' ')
          : 'Não foi possível validar o cartão com a operadora.';
        setError(msgs);
        setLoading(false);
        return;
      }

      // ─── Passo 2: Enviar apenas o TOKEN seguro para o nosso backend ──────
      await updateCreditCard({
        token: iuguData.id,
        description: form.name.trim(),
      });

      setSuccess(true);
      if (onSuccess) onSuccess();
    } catch (err) {
      const detail = err && err.error ? err.error : 'Ocorreu um erro ao atualizar o cartão.';
      setError(detail);
    } finally {
      setLoading(false);
    }
  }

  const displayNumber = maskDisplay(form.number);
  const [expM = 'MM', expY = 'AA'] = form.expiry.split('/');
  const displayName = form.name || 'NOME NO CARTÃO';

  return (
    <Modal title="Alterar Cartão de Crédito" show={show} onHide={handleClose}>

      {/* Preview do cartão */}
      <div style={S.cardPreview}>
        <div style={{ fontSize: 11, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 2 }}>Cartão de Crédito</div>
        <div style={S.cardNumber}>{displayNumber}</div>
        <div style={S.cardMeta}>
          <div>
            <div style={{ fontSize: 9, opacity: 0.7 }}>TITULAR</div>
            <div style={{ fontWeight: 600, textTransform: 'uppercase' }}>{displayName.toUpperCase()}</div>
          </div>
          <div>
            <div style={{ fontSize: 9, opacity: 0.7 }}>VALIDADE</div>
            <div style={{ fontWeight: 600 }}>{expM}/{expY}</div>
          </div>
        </div>
      </div>

      {/* Badge de segurança */}
      <div style={S.securityBadge}>
        <i className="fas fa-shield-alt" style={S.securityIcon} />
        <span>
          <strong>Seus dados não ficam armazenadas no Easygestor.</strong>
        </span>
      </div>

      {success ? (
        <div style={S.successBox}>
          <i className="fas fa-check-circle" style={{ marginRight: 6 }} />
          Cartão atualizado com sucesso! Ele será usado nas próximas cobranças da sua assinatura.
        </div>
      ) : (
        <form onSubmit={handleSubmit} style={S.form} autoComplete="off">

          {/* Número do cartão */}
          <div style={S.fieldGroup}>
            <label style={S.label}>Número do cartão</label>
            <input
              id="cc-number"
              style={Object.assign({}, S.input, focused === 'number' ? S.inputFocus : {})}
              name="number"
              value={form.number}
              onChange={handleChange}
              onFocus={function () { setFocused('number'); }}
              onBlur={function () { setFocused(''); }}
              placeholder="0000 0000 0000 0000"
              inputMode="numeric"
              autoComplete="cc-number"
              disabled={loading}
            />
          </div>

          {/* Nome no cartão */}
          <div style={S.fieldGroup}>
            <label style={S.label}>Nome impresso no cartão</label>
            <input
              id="cc-name"
              style={Object.assign({}, S.input, focused === 'name' ? S.inputFocus : {})}
              name="name"
              value={form.name}
              onChange={handleChange}
              onFocus={function () { setFocused('name'); }}
              onBlur={function () { setFocused(''); }}
              placeholder="Como aparece no cartão"
              autoComplete="cc-name"
              disabled={loading}
            />
          </div>

          {/* Validade e CVV lado a lado */}
          <div style={S.row}>
            <div style={S.fieldGroup}>
              <label style={S.label}>Validade</label>
              <input
                id="cc-exp"
                style={Object.assign({}, S.input, focused === 'expiry' ? S.inputFocus : {})}
                name="expiry"
                value={form.expiry}
                onChange={handleChange}
                onFocus={function () { setFocused('expiry'); }}
                onBlur={function () { setFocused(''); }}
                placeholder="MM/AA"
                inputMode="numeric"
                autoComplete="cc-exp"
                disabled={loading}
              />
            </div>
            <div style={S.fieldGroup}>
              <label style={S.label}>CVV</label>
              <input
                id="cc-csc"
                style={Object.assign({}, S.input, focused === 'cvv' ? S.inputFocus : {})}
                name="cvv"
                value={form.cvv}
                onChange={handleChange}
                onFocus={function () { setFocused('cvv'); }}
                onBlur={function () { setFocused(''); }}
                placeholder="123"
                inputMode="numeric"
                autoComplete="cc-csc"
                maxLength={4}
                disabled={loading}
              />
            </div>
          </div>

          {error && (
            <div style={S.errorBox}>
              <i className="fas fa-exclamation-circle" style={{ marginRight: 6 }} />
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            style={{ marginTop: 4, width: '100%' }}
          >
            {loading
              ? <React.Fragment><i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }} />Validando e salvando...</React.Fragment>
              : <React.Fragment><i className="fas fa-credit-card" style={{ marginRight: 6 }} />Salvar novo cartão</ React.Fragment>
            }
          </Button>
        </form>
      )}
    </Modal>
  );
}



// WEBPACK FOOTER //
// ./src/views/AppViews/Plans/ChangeCreditCardModal/index.jsx